import {
  AfterViewInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CanDisable, mixinColor, mixinDisabled, mixinTabIndex } from '../core';
import { FocusableOption, FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BooleanInput,
  coerceBooleanProperty,
  NumberInput
} from '@angular/cdk/coercion';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;

/**
 * Provider Expression that allows tix-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 */
export const TIX_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TixCheckboxComponent),
  multi: true
};

/**
 * Represents the different states that require custom transitions between them.
 */
export const enum TransitionCheckState {
  /** The initial state of the component before any user interaction. */
  Init,
  /** The state representing the component when it's becoming checked. */
  Checked,
  /** The state representing the component when it's becoming unchecked. */
  Unchecked
}

/** Change event object emitted by TixCheckbox. */
export class TixCheckboxChange {
  /** The source TixCheckbox of the event. */
  source: TixCheckboxComponent;
  /** The new `checked` value of the checkbox. */
  checked: boolean;
}

// Boilerplate for applying mixins to TixCheckbox.
const TixCheckboxBase = mixinTabIndex(
  mixinColor(
    mixinDisabled(
      class {
        constructor(public elementRef: ElementRef) {}
      }
    )
  )
);

/**
 * A tixerial design checkbox component. Supports all of the functionality of an HTML5 checkbox,
 * and exposes a similar API. A TixCheckbox can be either checked, unchecked, or
 * disabled. Note that all additional accessibility attributes are taken care of by the component,
 * so there is no need to provide them yourself. However, if you want to omit a label and still
 * have the checkbox be accessible, you may supply an [aria-label] input.
 * See: https://material.io/design/components/selection-controls.html
 */
@Component({
  selector: 'tix-checkbox',
  exportAs: 'tixCheckbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  host: {
    class: 'tix-checkbox',
    '[id]': 'id',
    '[attr.tabindex]': 'null',
    '[class.tix-checkbox-checked]': 'checked',
    '[class.tix-checkbox-disabled]': 'disabled',
    '[class.tix-checkbox-label-before]': 'labelPosition == "before"'
  },
  providers: [TIX_CHECKBOX_CONTROL_VALUE_ACCESSOR],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['color'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TixCheckboxComponent
  extends TixCheckboxBase
  implements
    ControlValueAccessor,
    AfterViewInit,
    OnDestroy,
    CanDisable,
    FocusableOption
{
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_required: BooleanInput;
  static ngAcceptInputType_tabIndex: NumberInput;

  private uniqueId = `tix-checkbox-${++nextUniqueId}`;

  /**
   * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
   * take precedence so this may be omitted.
   */
  @Input('aria-label') ariaLabel = '';

  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input('aria-labelledby') ariaLabelledby: string | null = null;

  /** The 'aria-describedby' attribute is read after the element's label and field type. */
  @Input('aria-describedby') ariaDescribedby: string;

  /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
  @Input() id: string = this.uniqueId;

  /** Returns the unique id for the visual hidden input. */
  get inputId(): string {
    return `${this.id || this.uniqueId}-input`;
  }

  /** Whether the checkbox is required. */
  @Input()
  get required(): boolean {
    return this.isRequired;
  }
  set required(value: boolean) {
    this.isRequired = coerceBooleanProperty(value);
  }
  private isRequired: boolean;

  /**
   * Whether the checkbox is checked.
   */
  @Input()
  get checked(): boolean {
    return this.isChecked;
  }
  set checked(value: boolean) {
    if (value != this.checked) {
      this.isChecked = value;
      this.changeDetectorRef.markForCheck();
    }
  }
  private isChecked = false;

  /**
   * Whether the checkbox is disabled. This fully overrides the implementation provided by
   * mixinDisabled, but the mixin is still required because mixinTabIndex requires it.
   */
  @Input()
  override get disabled() {
    return this.isDisabled;
  }
  override set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.disabled) {
      this.isDisabled = newValue;
      this.changeDetectorRef.markForCheck();
    }
  }
  private isDisabled = false;

  /** Whether the label should appear after or before the checkbox. Defaults to 'after' */
  @Input() labelPosition: 'before' | 'after' = 'after';

  /** Name value will be applied to the input element if present */
  @Input() name: string | null = null;

  /** Event emitted when the checkbox's `checked` value changes. */
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() readonly change: EventEmitter<TixCheckboxChange> =
    new EventEmitter<TixCheckboxChange>();

  /** The value attribute of the native input element */
  @Input() value: string;

  /** The native `<input type="checkbox">` element */
  @ViewChild('input') private inputElement: ElementRef<HTMLInputElement>;

  private currentCheckState: TransitionCheckState = TransitionCheckState.Init;

  /**
   * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
   */
  onTouched: () => any = () => void 0;

  private controlValueAccessorChangeFn: (value: any) => void = () => void 0;

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private changeDetectorRef: ChangeDetectorRef,
    private focusMonitor: FocusMonitor,
    private ngZone: NgZone,
    @Attribute('tabindex') tabIndex: string
  ) {
    super(elementRef);
    this.tabIndex = parseInt(tabIndex) || 0;
  }

  ngAfterViewInit() {
    this.focusMonitor.monitor(this.elementRef, true).subscribe(focusOrigin => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => {
          this.onTouched();
          this.changeDetectorRef.markForCheck();
        });
      }
    });
  }

  ngOnDestroy() {
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  /** Method being called whenever the label text changes. */
  onLabelTextChange() {
    // Since the event of the `cdkObserveContent` directive runs outside of the zone, the checkbox
    // component will be only marked for check, but no actual change detection runs automatically.
    // Instead of going back into the zone in order to trigger a change detection which causes
    // *all* components to be checked (if explicitly marked or not using OnPush), we only trigger
    // an explicit change detection for the checkbox view and its children.
    this.changeDetectorRef.detectChanges();
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: any) {
    this.checked = !!value;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void) {
    this.controlValueAccessorChangeFn = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  getAriaChecked(): 'true' | 'false' {
    return this.checked ? 'true' : 'false';
  }

  private transitionCheckState(newState: TransitionCheckState) {
    const oldState = this.currentCheckState;

    if (oldState === newState) {
      return;
    }

    this.currentCheckState = newState;
  }

  private emitChangeEvent() {
    const event = new TixCheckboxChange();
    event.source = this;
    event.checked = this.checked;

    this.controlValueAccessorChangeFn(this.checked);
    this.change.emit(event);

    // Assigning the value again here is redundant, but we have to do it in case it was
    // changed inside the `change` listener which will cause the input to be out of sync.
    if (this.inputElement) {
      this.inputElement.nativeElement.checked = this.checked;
    }
  }

  /** Toggles the `checked` state of the checkbox. */
  toggle(): void {
    this.checked = !this.checked;
  }

  /**
   * Event handler for checkbox input element.
   * Toggles checked state if element is not disabled.
   * Do not toggle on (change) event since IE doesn't fire change event when
   *   indeterminate checkbox is clicked.
   * @param event
   */
  onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `checkbox` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();

    // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
    if (!this.disabled) {
      this.toggle();
      this.transitionCheckState(
        this.isChecked
          ? TransitionCheckState.Checked
          : TransitionCheckState.Unchecked
      );

      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this.emitChangeEvent();
    }
  }

  /** Focuses the checkbox. */
  focus(origin?: FocusOrigin, options?: FocusOptions): void {
    if (origin) {
      this.focusMonitor.focusVia(this.inputElement, origin, options);
    } else {
      this.inputElement.nativeElement.focus(options);
    }
  }

  onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();
  }
}
